import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2,
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader, { GalleryUploader } from '@/components/CloudinaryUploader';

export const VideoGalleryModuleEditor = ({ data, onChange }) => {
  const videos = data.videos || [];
  const layout = data.layout || 'grid';
  const [editingIdx, setEditingIdx] = useState(null);
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [isLoadingPlaylist, setIsLoadingPlaylist] = useState(false);
  
  const addVideo = () => {
    const newVideo = { 
      id: Date.now().toString(),
      url: '', 
      title: '',
      platform: 'youtube',
      description: ''
    };
    onChange({...data, videos: [...videos, newVideo]});
    setEditingIdx(videos.length);
  };
  
  const updateVideo = (idx, updates) => {
    const updated = [...videos];
    updated[idx] = {...updated[idx], ...updates};
    onChange({...data, videos: updated});
  };
  
  const removeVideo = (idx) => {
    onChange({...data, videos: videos.filter((_, i) => i !== idx)});
    setEditingIdx(null);
  };

  // Extract YouTube video ID from various URL formats
  const getYoutubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match?.[1];
  };

  // Extract Vimeo video ID
  const getVimeoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:vimeo\.com\/)(\d+)/);
    return match?.[1];
  };

  // Detect platform from URL
  const detectPlatform = (url) => {
    if (!url) return 'youtube';
    if (url.includes('vimeo.com')) return 'vimeo';
    return 'youtube';
  };

  // Get thumbnail for any video
  const getThumbnail = (video) => {
    if (video.platform === 'vimeo' || video.url?.includes('vimeo')) {
      const vimeoId = getVimeoId(video.url);
      // Vimeo thumbnails require API, use placeholder
      return vimeoId ? `https://vumbnail.com/${vimeoId}.jpg` : null;
    }
    const ytId = getYoutubeId(video.url);
    return ytId ? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg` : null;
  };

  // Import YouTube playlist
  const importPlaylist = async () => {
    if (!playlistUrl) return;
    
    // Extract playlist ID
    const playlistMatch = playlistUrl.match(/[?&]list=([^&]+)/);
    if (!playlistMatch) {
      alert('Invalid playlist URL. Please use a YouTube playlist URL.');
      return;
    }
    
    setIsLoadingPlaylist(true);
    try {
      // Use YouTube's oembed to get playlist info (limited but works without API key)
      // For now, we'll add a placeholder - full implementation would need YouTube Data API
      const playlistId = playlistMatch[1];
      
      // Add the playlist as a single embed entry for now
      const newVideo = {
        id: Date.now().toString(),
        url: `https://www.youtube.com/playlist?list=${playlistId}`,
        title: 'YouTube Playlist',
        platform: 'youtube_playlist',
        playlistId: playlistId,
        description: 'Full playlist embed'
      };
      
      onChange({...data, videos: [...videos, newVideo]});
      setPlaylistUrl('');
      alert('Playlist added! The entire playlist will be embedded on your site.');
    } catch (error) {
      console.error('Failed to import playlist:', error);
      alert('Failed to import playlist. Please try again.');
    } finally {
      setIsLoadingPlaylist(false);
    }
  };

  // Single video editor panel
  const renderEditor = (video, idx) => (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-white">Edit Video</h4>
        <Button variant="ghost" size="sm" onClick={() => setEditingIdx(null)} className="text-slate-400 hover:text-white">Done</Button>
      </div>
      
      <div className="space-y-2">
        <Label>Video URL</Label>
        <Input 
          value={video.url} 
          onChange={(e) => {
            const url = e.target.value;
            const platform = detectPlatform(url);
            updateVideo(idx, { url, platform });
          }}
          placeholder="YouTube or Vimeo URL"
        />
        <p className="text-xs text-slate-400">
          Supports YouTube (youtube.com, youtu.be) and Vimeo (vimeo.com)
        </p>
      </div>
      
      <div className="space-y-2">
        <Label>Title</Label>
        <Input 
          value={video.title || ''} 
          onChange={(e) => updateVideo(idx, { title: e.target.value })}
          placeholder="Video title"
        />
      </div>
      
      <div className="space-y-2">
        <Label>Description (optional)</Label>
        <Textarea 
          value={video.description || ''} 
          onChange={(e) => updateVideo(idx, { description: e.target.value })}
          placeholder="Brief description..."
          rows={2}
        />
      </div>

      {/* Preview */}
      {getThumbnail(video) && (
        <div className="space-y-2">
          <Label>Preview</Label>
          <div className="aspect-video rounded-lg overflow-hidden bg-slate-700">
            <img src={getThumbnail(video)} alt="Thumbnail" className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <div className="pt-2 border-t border-slate-700">
        <Button variant="ghost" size="sm" onClick={() => removeVideo(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/20">
          <Trash2 className="w-4 h-4 mr-2" /> Delete Video
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'Videos'} 
          onChange={(e) => onChange({...data, title: e.target.value})}
        />
      </div>

      {/* Layout Options */}
      <div className="space-y-3">
        <Label>Display Layout</Label>
        <div className="flex items-center gap-2">
          {['grid', 'list', 'featured'].map(l => (
            <button
              key={l}
              onClick={() => onChange({...data, layout: l})}
              className={cn(
                "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all capitalize",
                layout === l ? "bg-orange-500/20 border-orange-500 text-orange-400" : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
              )}
            >
              <span className="text-xs">{l}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* YouTube Playlist Import */}
      <div className="p-4 bg-red-900/20 border border-red-800/50 rounded-lg space-y-3">
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5 text-red-400" />
          <Label className="text-red-300 font-medium">Import YouTube Playlist</Label>
        </div>
        <div className="flex gap-2">
          <Input 
            value={playlistUrl} 
            onChange={(e) => setPlaylistUrl(e.target.value)}
            placeholder="https://www.youtube.com/playlist?list=..."
            className="flex-1"
          />
          <Button 
            onClick={importPlaylist} 
            disabled={isLoadingPlaylist || !playlistUrl}
            className="bg-red-600 hover:bg-red-700"
          >
            {isLoadingPlaylist ? 'Loading...' : 'Import'}
          </Button>
        </div>
        <p className="text-xs text-red-300/70">
          Paste a YouTube playlist URL to embed the entire playlist
        </p>
      </div>

      {/* Individual Videos */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Videos ({videos.length})</Label>
          <Button variant="outline" size="sm" onClick={addVideo}>
            <Plus className="w-4 h-4 mr-1" /> Add Video
          </Button>
        </div>
        
        {videos.length === 0 ? (
          <div className="text-center py-12 bg-slate-700/30 rounded-lg border border-slate-600">
            <Video className="w-10 h-10 mx-auto text-slate-500 mb-3" />
            <p className="text-sm text-slate-400">No videos yet. Add YouTube or Vimeo videos!</p>
          </div>
        ) : editingIdx !== null && videos[editingIdx] ? (
          renderEditor(videos[editingIdx], editingIdx)
        ) : (
          <div className="space-y-2">
            {videos.map((video, idx) => {
              const thumbnail = getThumbnail(video);
              const isPlaylist = video.platform === 'youtube_playlist';
              return (
                <div 
                  key={video.id || idx} 
                  onClick={() => setEditingIdx(idx)}
                  className="flex items-center gap-4 p-3 bg-slate-700/30 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700/50 hover:border-slate-500 transition-all group"
                >
                  {/* Thumbnail */}
                  <div className="w-24 h-14 rounded bg-slate-600 overflow-hidden flex-shrink-0 relative">
                    {thumbnail ? (
                      <img src={thumbnail} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-6 h-6 text-slate-500" />
                      </div>
                    )}
                    {isPlaylist && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="text-xs text-white font-medium">PLAYLIST</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-white truncate">{video.title || 'Untitled Video'}</h4>
                    <p className="text-xs text-slate-400 flex items-center gap-2">
                      <span className={cn(
                        "px-1.5 py-0.5 rounded text-[10px] font-medium uppercase",
                        video.platform === 'vimeo' ? "bg-blue-500/20 text-blue-400" : "bg-red-500/20 text-red-400"
                      )}>
                        {isPlaylist ? 'YouTube Playlist' : video.platform || 'YouTube'}
                      </span>
                      <span className="truncate">{video.url?.substring(0, 40)}...</span>
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeVideo(idx); }} className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// News Module Editor

